import { AxiosError } from "axios";
import { router } from "@/plugins/1.router";
import { useNotificationStore } from "@/store/notification";
import { userStore } from "@/store/user";

interface ErrorMessage {
  title: string;
  message: string;
}

type ErrorMessages = {
  [key: string]: ErrorMessage;
};

const errorMessages: ErrorMessages = {
  "phone must be a valid phone number": {
    title: "Telefone inválido",
    message: "Verifique o número digitado e tente novamente",
  },
  "userName must be longer than or equal to 4 characters": {
    title: "Nome de usuário inválido",
    message: "O nome de usuário deve conter pelo menos 4 caracteres",
  },
  "userName should not be empty": {
    title: "Campo obrigatório",
    message: "O nome de usuário não pode estar vazio",
  },
  "email must be an email": {
    title: "E-mail inválido",
    message: "Verifique o e-mail digitado e tente novamente",
  },
  "Password must have at least 1 number": {
    title: "Senha inválida",
    message: "A senha deve conter pelo menos 1 número",
  },
  "password must be longer than or equal to 6 characters": {
    title: "Senha inválida",
    message: "A senha deve conter pelo menos 6 caracteres",
  },
  "Password must have at least 1 special character": {
    title: "Senha inválida",
    message: "A senha deve conter pelo menos 1 caractere especial",
  },
  "Password must have at least 1 lowercase letter": {
    title: "Senha inválida",
    message: "A senha deve conter pelo menos 1 letra minúscula",
  },
  "The password must have at least 1 uppercase letter": {
    title: "Senha inválida",
    message: "A senha deve conter pelo menos 1 letra maiúscula",
  },
  "userName must be shorter than or equal to 20 characters": {
    title: "Nome muito longo",
    message: "O nome de usuário deve conter no máximo 20 caracteres",
  },
  "password must be shorter than or equal to 20 characters": {
    title: "Senha muito longa",
    message: "A senha deve conter no máximo 20 caracteres",
  },
  "E-mail is already in use": {
    title: "E-mail já cadastrado",
    message: "Utilize outro e-mail para continuar",
  },
  "Token is invalid": {
    title: "Token inválido",
    message: "Verifique o token e tente novamente",
  },
  "There is no token for this email": {
    title: "Token não encontrado",
    message: "O token de redefinição de senha expirou ou é inválido",
  },
  "Specialty not found": {
    title: "Especialidade não encontrada",
    message: "A especialidade solicitada não foi encontrada",
  },
};

function getErrorMessage(message: string): ErrorMessage | null {
  return errorMessages[message] || null;
}

function handle400Error(message: any, notificationStore: any) {
  if (Array.isArray(message)) {
    message.forEach((msg: string) => {
      const errorMsg = getErrorMessage(msg);
      if (errorMsg) {
        notificationStore.notifyFail({
          title: errorMsg.title,
          text: errorMsg.message,
        });
      } else {
        notificationStore.notifyFail({
          title: "Dados inválidos",
          text: msg || "Verifique os dados informados e tente novamente",
        });
      }
    });
  } else {
    const errorMsg = getErrorMessage(message);
    if (errorMsg) {
      notificationStore.notifyFail({
        title: errorMsg.title,
        text: errorMsg.message,
      });
    } else {
      notificationStore.notifyFail({
        title: "Erro na requisição",
        text:
          message ||
          "Os dados fornecidos são inválidos. Verifique e tente novamente.",
      });
    }
  }
}

export function errorHandler(axiosError: AxiosError) {
  const notificationStore = useNotificationStore();

  if (!axiosError.response) {
    notificationStore.notifyFail({
      title: "Erro de conexão",
      text: "Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.",
    });
    console.error(axiosError);
    return Promise.reject(axiosError);
  }

  const { error, statusCode, message } = axiosError.response?.data as any;

  let title: string | null = null;
  let text: string | null = null;

  switch (statusCode) {
    case 400: {
      handle400Error(message, notificationStore);
      break;
    }
    case 401: {
      title = "Erro de autenticação";
      text = "Verifique suas credenciais e tente novamente";

      // Evitar logout automático em rotas públicas
      const currentRoute = router.currentRoute?.value;
      const isPublicRoute =
        (currentRoute?.meta as any)?.requireAuth === false ||
        (currentRoute?.path || "").startsWith("/public/");

      if (!isPublicRoute) {
        const user = userStore();
        user.logout();
      }
      break;
    }
    case 403: {
      notificationStore.notifyWarning({
        title: "Acesso negado",
        text: "Você não tem permissão para realizar esta ação",
      });
      break;
    }
    case 404: {
      // Para rotas de assinatura, 404 é esperado (empresa pode não ter assinatura)
      // Não mostrar notificação nem logar erro para evitar spam
      const url = (axiosError.config?.url || "").toLowerCase();
      if (url.includes("/billing/subscription/") && !url.includes("/all")) {
        // Assinatura não encontrada é um caso esperado, não mostrar erro nem logar
        // Retornar sem logar para evitar poluição do console
        return Promise.reject(axiosError.response?.data);
      }
      title = "Recurso não encontrado";
      text = "O recurso solicitado não foi encontrado";
      break;
    }
    case 422: {
      handle400Error(message, notificationStore);
      break;
    }
    case 428: {
      notificationStore.notifyWarning({
        title: "Verificação pendente",
        text: "Valide seu celular para continuar",
      });
      router.push("/auth/confirm-code");
      break;
    }
    case 429: {
      title = "Muitas tentativas";
      text =
        "Você realizou muitas tentativas. Aguarde alguns minutos e tente novamente.";
      break;
    }
    case 500: {
      title = "Erro interno do servidor";
      text = "Ocorreu um erro interno. Tente novamente mais tarde.";
      break;
    }
    default: {
      if (Array.isArray(message)) {
        message.forEach((msg: string) => {
          const errorMsg = getErrorMessage(msg);
          if (errorMsg) {
            notificationStore.notifyFail({
              title: errorMsg.title,
              text: errorMsg.message,
            });
          } else {
            notificationStore.notifyFail({
              title: "Erro",
              text: msg || "Ocorreu um erro inesperado",
            });
          }
        });
      } else {
        const errorMsg = getErrorMessage(message);
        if (errorMsg) {
          title = errorMsg.title;
          text = errorMsg.message;
        } else {
          title = "Erro inesperado";
          text = message || "Ocorreu um erro inesperado. Tente novamente.";
        }
      }
    }
  }

  if (title && text) {
    notificationStore.notifyFail({ title, text });
  }

  // Não logar erro para 404 de assinatura (caso esperado)
  const url = (axiosError.config?.url || "").toLowerCase();
  const isSubscription404 =
    statusCode === 404 &&
    url.includes("/billing/subscription/") &&
    !url.includes("/all");

  if (!isSubscription404) {
    console.error("Error details:", { statusCode, message, error });
  }

  return Promise.reject(axiosError.response?.data);
}
